import { getMomentTz } from "@dexteel/mesf-core";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Box,
	Chip,
	IconButton,
	Stack,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { TEST_SHACK_REPORT_STATUS } from "@/pages/products/test-shack/models/TestShackReport";
import type { TestShackReportSectionRevRow } from "@/pages/products/test-shack/models/TestShackReportSectionRev";
import {
	formatMeasurement,
	formatOutOfSpec,
	isOutOfSpec,
} from "@/pages/products/test-shack/utils/reportFormat";
import { MeasurementValue } from "../MeasurementValue";
import { TestShackReportStatusArrow } from "./TestShackReportStatusArrow";

const moment = getMomentTz();

interface MetaFieldProps {
	label: string;
	children: ReactNode;
	sx?: SxProps<Theme>;
}

const MetaField = ({ label, children, sx }: MetaFieldProps) => (
	<Box sx={{ minWidth: 0, ...sx, mr: 1 }}>
		<Typography
			sx={{
				color: "text.disabled",
				fontSize: "9px",
				fontWeight: 700,
				letterSpacing: "0.06em",
				lineHeight: 1.4,
				textTransform: "uppercase",
			}}
		>
			{label}
		</Typography>
		<Stack
			direction="row"
			spacing={0.5}
			alignItems="center"
			sx={{ lineHeight: 1.5, minWidth: 0 }}
		>
			{children}
		</Stack>
	</Box>
);

interface TestShackReportInspectionMetaRowProps {
	row: Pick<
		TestShackReportSectionRevRow,
		"inspection" | "outOfSpecCount" | "hasTolerances"
	>;
	columnCount: number;
	managerRoute: string;
}

export const TestShackReportInspectionMetaRow = ({
	row,
	columnCount,
	managerRoute,
}: TestShackReportInspectionMetaRowProps) => {
	const navigate = useNavigate();
	const { inspection } = row;
	// Without a specification nothing can be judged, so nothing is flagged.
	const isEvaluated = row.hasTolerances;
	const hasIssues = isEvaluated && row.outOfSpecCount > 0;
	const weightStatus = inspection.WeightStatus ?? TEST_SHACK_REPORT_STATUS.IN;
	const hasWeightIssue =
		isEvaluated &&
		inspection.WeightStatus !== null &&
		isOutOfSpec(inspection.WeightStatus);
	const shift = [
		inspection.Shift,
		inspection.Crew ? `Crew ${inspection.Crew}` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<TableRow
			data-test-id={`report-inspection-${inspection.ProfileInspectionId}`}
		>
			<TableCell
				colSpan={columnCount}
				sx={{
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
					borderTop: 1,
					borderTopColor: "divider",
					px: 1,
					py: 0.5,
				}}
			>
				<Stack
					direction="row"
					spacing={2}
					alignItems="flex-end"
					flexWrap="wrap"
					useFlexGap
				>
					<Typography
						sx={{
							alignSelf: "center",
							fontSize: "12px",
							fontWeight: 800,
							lineHeight: 1.5,
						}}
					>
						#{inspection.ProfileInspectionId}
					</Typography>
					<MetaField label="Shift">
						<Typography sx={{ fontSize: "12px" }}>
							{moment.utc(inspection.ProductionDate).format("MM/DD/YY")}
							{shift ? ` · ${shift}` : ""}
						</Typography>
					</MetaField>
					<MetaField label="Heat">
						<Typography sx={{ fontSize: "12px" }}>
							{inspection.HeatName ?? "-"}
						</Typography>
					</MetaField>
					<MetaField label="Inspector">
						<Typography sx={{ fontSize: "12px" }}>
							{inspection.Inspector ?? "-"}
						</Typography>
					</MetaField>
					<MetaField label="lb/ft">
						<Typography
							sx={{
								fontFamily: "monospace",
								fontSize: "12px",
								fontVariantNumeric: "tabular-nums",
								whiteSpace: "nowrap",
								color:
									hasWeightIssue ||
									(isEvaluated && inspection.MeasuredLbsPerFoot === null)
										? "error.main"
										: "text.primary",
								fontWeight: hasWeightIssue ? 700 : 400,
							}}
						>
							<MeasurementValue>
								{formatMeasurement(inspection.MeasuredLbsPerFoot)}
							</MeasurementValue>
						</Typography>
						{hasWeightIssue && (
							<TestShackReportStatusArrow status={weightStatus} />
						)}
					</MetaField>
					<MetaField label="Comments" sx={{ flex: 1, minWidth: 140 }}>
						<Typography
							color="text.secondary"
							fontStyle="italic"
							noWrap
							title={inspection.Comments ?? undefined}
							sx={{ fontSize: "12px" }}
						>
							{inspection.Comments ?? "No comments"}
						</Typography>
					</MetaField>
					{/* Tolerances belong to the Section-Rev, so its card shows "No Specification". */}
					{isEvaluated && (
						<Chip
							size="small"
							label={formatOutOfSpec(row)}
							color={hasIssues ? "error" : "success"}
							variant={hasIssues ? "filled" : "outlined"}
							sx={{
								alignSelf: "center",
								fontSize: "10px",
								fontWeight: 700,
								height: 20,
							}}
						/>
					)}
					<Tooltip title="Open the shift in the Test Shack Manager">
						<IconButton
							size="small"
							color="primary"
							aria-label={`Open inspection ${inspection.ProfileInspectionId} in the manager`}
							onClick={() =>
								navigate(
									`${managerRoute}?mode=shift&shift=${inspection.ShiftId}&inspection=${inspection.ProfileInspectionId}`,
								)
							}
							sx={{ alignSelf: "center" }}
						>
							<OpenInNewIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				</Stack>
			</TableCell>
		</TableRow>
	);
};
